'use client'

import { useId, useState } from 'react'

import styles from './RangeSliderField.module.scss'
import type { RangeSliderFieldProps, RangeSliderValue } from './RangeSliderField.types'

const DEFAULT_STEP = 1

/** Teclas que confirman un movimiento de teclado (el resto de `keyup` no
 * corresponde a un cambio de valor del `<input type="range">`). */
const COMMIT_KEYS = new Set(['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End', 'PageUp', 'PageDown'])

export function defaultFormatValue(value: number): string {
  return new Intl.NumberFormat('es-ES', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value)
}

/** Clampea el par al rango `[min, max]` y garantiza `min ≤ max`. */
function clampRange(next: RangeSliderValue, bounds: { min: number; max: number }): RangeSliderValue {
  const clampedMin = Math.min(Math.max(next.min, bounds.min), bounds.max)
  const clampedMax = Math.min(Math.max(next.max, bounds.min), bounds.max)
  return { min: Math.min(clampedMin, clampedMax), max: Math.max(clampedMin, clampedMax) }
}

/**
 * Slider de rango continuo (doble asa) del DS: dos `<input type="range">`
 * nativos superpuestos, en vez de un widget custom con `role="slider"`
 * reimplementado a mano — el teclado (flechas/Home/End/Page Up/Down), el foco
 * y el anuncio de valor (`aria-valuetext`) los da el navegador gratis (ver
 * `specs/022-promociones-plp/research.md` §4).
 *
 * Filtra solo al soltar/confirmar (`onChangeEnd`): mientras se arrastra, el
 * valor mostrado se actualiza en un estado local (`draft`) sin invocar el
 * callback — evita disparar una petición de red en cada paso intermedio.
 */
export function RangeSliderField({
  label,
  min,
  max,
  value,
  step = DEFAULT_STEP,
  disabled = false,
  variant = 'default',
  onChangeEnd,
  formatValue = defaultFormatValue,
  id,
}: RangeSliderFieldProps) {
  const generatedId = useId()
  const fieldId = id ?? generatedId
  const [draft, setDraft] = useState<RangeSliderValue>(value)
  // Última `value` (controlada) ya reflejada en `draft` — permite detectar un
  // cambio externo real (navegación, "Solo gratuitos", límites que cambian
  // con la combinación de filtros activa) y ajustar el estado DURANTE el
  // render en vez de en un efecto posterior (evita el render en cascada que
  // `setState` síncrono dentro de un efecto produciría — ver
  // `.claude/rules/architecture.rule.md`, distinto del caso "corregir con una
  // fuente solo-navegador" que sí exige un efecto). Nunca interrumpe un
  // arrastre en curso: `value` solo cambia una vez que un `onChangeEnd`
  // anterior ya se resolvió.
  const [syncedValue, setSyncedValue] = useState<RangeSliderValue>(value)
  if (value.min !== syncedValue.min || value.max !== syncedValue.max) {
    setSyncedValue(value)
    setDraft(value)
  }

  function commit(next: RangeSliderValue) {
    const clamped = clampRange(next, { min, max })
    setDraft(clamped)
    onChangeEnd(clamped)
  }

  function handleMinDrag(rawValue: number) {
    setDraft((current) => ({ min: Math.min(rawValue, current.max), max: current.max }))
  }

  function handleMaxDrag(rawValue: number) {
    setDraft((current) => ({ min: current.min, max: Math.max(rawValue, current.min) }))
  }

  function handleKeyUp(event: React.KeyboardEvent<HTMLInputElement>) {
    if (COMMIT_KEYS.has(event.key)) commit(draft)
  }

  const span = max - min || 1
  const minPercent = ((draft.min - min) / span) * 100
  const maxPercent = ((draft.max - min) / span) * 100

  return (
    <div className={styles.root} data-variant={variant} data-disabled={disabled || undefined}>
      <span className={styles.label} id={`${fieldId}-label`}>
        {label}
      </span>
      <div className={styles.values}>
        <span>{formatValue(draft.min)}</span>
        <span>{formatValue(draft.max)}</span>
      </div>
      <div className={styles.track}>
        <div className={styles.trackBase} />
        <div
          className={styles.trackHighlight}
          style={{ left: `${minPercent}%`, right: `${100 - maxPercent}%` }}
        />
        <div className={styles.inputs}>
          <input
            type="range"
            className={styles.rangeInput}
            id={`${fieldId}-min`}
            aria-label={`${label} mínimo`}
            aria-valuetext={formatValue(draft.min)}
            min={min}
            max={max}
            step={step}
            value={draft.min}
            disabled={disabled}
            onChange={(event) => handleMinDrag(Number(event.target.value))}
            onPointerUp={() => commit(draft)}
            onBlur={() => commit(draft)}
            onKeyUp={handleKeyUp}
          />
          <input
            type="range"
            className={styles.rangeInput}
            id={`${fieldId}-max`}
            aria-label={`${label} máximo`}
            aria-valuetext={formatValue(draft.max)}
            min={min}
            max={max}
            step={step}
            value={draft.max}
            disabled={disabled}
            onChange={(event) => handleMaxDrag(Number(event.target.value))}
            onPointerUp={() => commit(draft)}
            onBlur={() => commit(draft)}
            onKeyUp={handleKeyUp}
          />
        </div>
      </div>
    </div>
  )
}
